import { useState } from 'react';
import { Download, FileSpreadsheet, FileText, FileJson, FileType } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { saveFile, type ExportedFile } from '@/lib/download';
import { Button, Card, CardHeader, Field, LoadingBlock, Select, useToast } from './ui';

interface ExportOptions {
  formats: string[];
  datasets: { key: string; label: string }[];
  max_rows: number;
}

const FORMAT_HELP: Record<string, { label: string; hint: string; icon: JSX.Element }> = {
  csv: {
    label: 'Spreadsheet (CSV)',
    hint: 'Opens in Excel, Numbers or Google Sheets. The one to use for an accountant or another system.',
    icon: <FileSpreadsheet className="h-4 w-4" />,
  },
  pdf: {
    label: 'Document (PDF)',
    hint: 'For printing, attaching to an email, or handing to somebody who will not open a spreadsheet.',
    icon: <FileType className="h-4 w-4" />,
  },
  txt: {
    label: 'Plain text',
    hint: 'One field per line. For pasting into an email or a note.',
    icon: <FileText className="h-4 w-4" />,
  },
  json: {
    label: 'Data file (JSON)',
    hint: 'Every column exactly as stored, ids and all. For a developer or another product’s importer.',
    icon: <FileJson className="h-4 w-4" />,
  },
};

/**
 * Your records, out of PROVIA and into a file.
 *
 * Here because it is the answer to "what if we want to leave?", and software
 * that makes leaving hard is software people are right to distrust. It is also
 * what the lapsed-account notice already promises in writing: nothing is
 * deleted, and everything can still be read and exported.
 *
 * Every role can use it, a lapsed account included. Exporting is a read, and
 * holding somebody's own records hostage over a card that expired is not a
 * business PROVIA is in.
 */
export function DataExport() {
  const toast = useToast();
  const { data, loading } = useApi<ExportOptions>('/export');
  const [dataset, setDataset] = useState('tenants');
  const [format, setFormat] = useState('csv');
  const [busy, setBusy] = useState(false);

  async function run() {
    setBusy(true);
    try {
      const file = await api.post('/export', { dataset, format }) as ExportedFile & {
        rows: number; total: number; truncated: boolean; label: string;
      };
      saveFile(file);
      toast.success(
        file.truncated
          ? `Exported the most recent ${file.rows} of ${file.total} ${file.label.toLowerCase()}`
          : `Exported ${file.rows} ${file.rows === 1 ? 'record' : 'records'}`,
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not build that export');
    } finally {
      setBusy(false);
    }
  }

  if (loading && !data) return <LoadingBlock rows={3} />;
  if (!data) return null;

  const help = FORMAT_HELP[format];

  return (
    <Card>
      <CardHeader
        title="Export your data"
        subtitle="Your records are yours. Take a copy whenever you like."
      />
      <div className="p-5 sm:p-6 pt-4 space-y-5 max-w-2xl">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="What to export">
            <Select value={dataset} onChange={(e) => setDataset(e.target.value)}>
              {data.datasets.map((d) => (
                <option key={d.key} value={d.key}>{d.label}</option>
              ))}
            </Select>
          </Field>
          <Field label="As">
            <Select value={format} onChange={(e) => setFormat(e.target.value)}>
              {data.formats.map((f) => (
                <option key={f} value={f}>{FORMAT_HELP[f]?.label ?? f.toUpperCase()}</option>
              ))}
            </Select>
          </Field>
        </div>

        {help && (
          <p className="text-sm text-muted flex items-start gap-2">
            <span className="text-faint mt-0.5">{help.icon}</span>
            <span>{help.hint}</span>
          </p>
        )}

        <div className="flex items-center gap-3">
          <Button
            variant="primary"
            onClick={() => void run()}
            loading={busy}
            icon={<Download className="h-4 w-4" />}
          >
            Download
          </Button>
          <p className="text-xs text-faint">
            {/*
              Said before it happens rather than discovered afterwards in a file
              that is quietly missing the oldest half of somebody's history.
            */}
            Up to {data.max_rows.toLocaleString()} records at a time, newest first.
            Every export is recorded in your audit log.
          </p>
        </div>
      </div>
    </Card>
  );
}
